/**
 * The writes that resolve an item without a queue response — and the rule that none of them is
 * optimistic.
 *
 * `POST /survey/assignment/{id}/mark-reviewed` makes the item stop being true on the server
 * (`intake-survey` R28): what the attention-queue requirements call implicit resolution (R15). The
 * other is the session note, which `features/session-comment` writes — and whose `onSaved` fires
 * only inside its own PATCH's `onSuccess`, so that path was already safe. A clip is resolved by the
 * full-screen player on the client's card, which marks it seen; the desk only links there.
 *
 * ── The card is struck only once the SERVER has agreed ───────────────────────────────────────
 * There is nothing to withdraw, so there is nothing to be optimistic with. `model/implicitClose`
 * carries the reasoning and the defect it is named after. While a write is in flight the primary
 * says so and will not take a second press; if it is refused the card stays exactly as it was and
 * the refusal is said in BOTH channels — the screen's live region, and in ink on the card the
 * trainer is looking at, because this app has no toasts and one is not being added.
 *
 * ── None of them is undoable ────────────────────────────────────────────────────────────────
 * The hold exists because `/attention-queue/responses` has no inverse; these have already happened
 * once they succeed, and offering `Z` on one would take the card back onto a queue the server has
 * stopped returning.
 *
 * ── "Oznacz jako przejrzane" is NOT "Gotowe" ────────────────────────────────────────────────
 * `ReviewedAt` is visible to the CLIENT (`intake-survey` R31) and a queue response is private to
 * the responder (R4). The two controls are drawn separately and never one under the other's name.
 *
 * ── Why these are not `useQueueCard`'s ──────────────────────────────────────────────────────
 * The Pulpit has the same endpoint, wrapped in its own focus landings, its `headingRef` and
 * the two refs that decide whether the keyboard may be moved after the server answers. What is
 * shared between the two screens is one generated function call each; everything around them
 * differs, and a module holding only the call would be a wrapper that forwards.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { attentionQueueQueryKey } from '~/entities/client';
import { postSurveyAssignmentIdMarkReviewed } from '~/shared/api/generated/endpoints/survey/survey';

import {
  implicitCloseIsSending,
  implicitCloseRefused,
  implicitCloseSucceeded,
  implicitCloseWasRefused,
  NOTHING_CLOSING,
  startImplicitClose,
} from '~/features/attention-queue';

export interface ImplicitResolution {
  /** Mark the survey reviewed. Resolves the card only once the server has agreed. */
  readonly markReviewed: (key: string, surveyAssignmentId: number, task: string) => void;
  /** The note saved. `features/session-comment` did the write, and only calls back on success. */
  readonly noteSaved: (key: string, task: string) => void;
  /** This card's write is in flight: the primary says so and refuses a second press. */
  readonly isSending: (key: string) => boolean;
  /** The server refused this card's write: the card prints the sentence beside its primary. */
  readonly wasRefused: (key: string) => boolean;
}

export interface ImplicitResolutionDeps {
  /**
   * The server agreed: strike this card. Never a queue response.
   *
   * `keepOnDesk` is the clip's: the trainer is watching it, so the desk stays where it is. See
   * `model/taskDeck`'s `resolveImplicitly`.
   */
  readonly onResolved: (key: string, options: { readonly keepOnDesk: boolean }) => void;
  readonly announce: (message: string) => void;
}

interface Close {
  readonly key: string;
  readonly task: string;
}

export function useImplicitResolution({
  onResolved,
  announce,
}: ImplicitResolutionDeps): ImplicitResolution {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [closing, setClosing] = useState(NOTHING_CLOSING);

  const succeeded = (key: string, keepOnDesk: boolean) => {
    setClosing((current) => implicitCloseSucceeded(current, key));
    onResolved(key, { keepOnDesk });
  };
  const refused = (key: string) => {
    setClosing((current) => implicitCloseRefused(current, key));
    announce(t('web:home.live.quickFailed'));
  };

  const reviewed = useMutation({
    mutationFn: ({ surveyAssignmentId }: Close & { surveyAssignmentId: number }) =>
      postSurveyAssignmentIdMarkReviewed(surveyAssignmentId),
    onSuccess: (_answer, { key, task }) => {
      announce(t('web:home.live.reviewed', { task }));
      void queryClient.invalidateQueries({ queryKey: attentionQueueQueryKey });
      succeeded(key, false);
    },
    onError: (_error, { key }) => {
      refused(key);
    },
  });

  /** Take the gate for this card, or answer `false` because a write is already in the air. */
  const begin = (key: string): boolean => {
    const next = startImplicitClose(closing, key);
    if (next === null) return false;
    setClosing(next);
    return true;
  };

  return {
    markReviewed: (key, surveyAssignmentId, task) => {
      if (begin(key)) reviewed.mutate({ key, task, surveyAssignmentId });
    },
    noteSaved: (key, task) => {
      // `useSessionComment` already invalidates the attention queue inside its own `onSuccess`, so
      // nothing is invalidated again here — the endpoint is limited to 30 reads a minute.
      announce(t('web:home.live.noteSaved', { task }));
      onResolved(key, { keepOnDesk: false });
    },
    isSending: (key) => implicitCloseIsSending(closing, key),
    wasRefused: (key) => implicitCloseWasRefused(closing, key),
  };
}
